<!--
  SupportEmailCard -- the Super Admin control for the `support-email` platform
  setting (card #573, Jake 2026-10-29): the address customers are told to
  write to for help (for example the DTF low-resolution note shown when art
  cleanup is switched off, and that flow's refusal sentences).

  GET/PATCH /api/platform-settings/support-email. The server owns the value in
  force, the default and the validation (one plain address, no list or display
  name, at most 254 characters); this card shows the server's sentence on a
  refusal. Reset sends null, which puts the default back.

  Kept as its own component (like DtfArtCleanupCard) so the Platform Settings
  page only mounts it.
-->
<template>
    <div class="sec-card" data-testid="sa-setting-support-email">
        <div class="sec-card__copy">
            <label class="sec-card__label" for="sa-setting-support-email-input">
                Support email address
            </label>
            <p class="sec-card__help" data-testid="sa-setting-support-email-help">
                The address customers are told to write to for help, for
                example in the low-resolution note on a DTF image when art
                cleanup is switched off. One plain address; no list and no
                name.
            </p>
        </div>

        <template v-if="loaded">
            <div class="sec-field">
                <input
                    id="sa-setting-support-email-input"
                    v-model="input"
                    type="text"
                    inputmode="email"
                    autocomplete="off"
                    class="sec-field__input"
                    data-testid="sa-setting-support-email-input"
                    :disabled="saving"
                    @keyup.enter="onSave"
                />
                <p class="sec-card__note" data-testid="sa-setting-support-email-in-force">
                    In force: {{ server.value }}
                </p>
                <p class="sec-card__note" data-testid="sa-setting-support-email-default">
                    Default {{ server.default }}.
                </p>
            </div>
            <div class="sec-actions">
                <button
                    type="button"
                    class="sec-button"
                    data-testid="sa-setting-support-email-save"
                    :disabled="saving"
                    @click="onSave"
                >
                    Save
                </button>
                <button
                    type="button"
                    class="sec-button"
                    data-testid="sa-setting-support-email-reset"
                    :disabled="saving"
                    @click="patch(null)"
                >
                    Reset to default
                </button>
            </div>
        </template>

        <p v-if="saved" class="sec-card__note" data-testid="sa-setting-support-email-saved">
            Saved.
        </p>
        <p v-if="error" class="sec-card__error" data-testid="sa-setting-support-email-error">
            {{ error }}
        </p>
    </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axios from "@/axios";

const URL = "/api/platform-settings/support-email";

const loaded = ref(false);
const server = ref({ value: "", default: "" });
const input = ref("");
const saving = ref(false);
const saved = ref(false);
const error = ref("");

function apply(data) {
    server.value = data;
    input.value = data.value;
    loaded.value = true;
}

const shapeOk = (d) => typeof d?.value === "string" && typeof d?.default === "string";

async function load() {
    error.value = "";
    try {
        const resp = await axios.get(URL);
        if (!shapeOk(resp?.data)) throw new Error("bad shape");
        apply(resp.data);
    } catch (_e) {
        loaded.value = false;
        error.value = "Could not load the support email setting.";
    }
}

async function patch(value) {
    saving.value = true;
    error.value = "";
    saved.value = false;
    try {
        const resp = await axios.patch(URL, { value });
        if (!shapeOk(resp?.data)) throw new Error("bad shape");
        apply(resp.data);
        saved.value = true;
    } catch (e) {
        const status = e?.response?.status;
        if (status === 403) {
            error.value = "Permission denied. Only Super Admins can change this setting.";
        } else if (status === 422 || status === 400) {
            error.value =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                "That address was rejected by the server.";
        } else {
            error.value = "Could not save this setting.";
        }
    } finally {
        saving.value = false;
    }
}

async function onSave() {
    const raw = String(input.value ?? "").trim();
    if (!raw) {
        saved.value = false;
        error.value = "Enter one email address, like help@example.com.";
        return;
    }
    await patch(raw);
}

onMounted(load);
</script>

<style scoped>
.sec-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.sec-card__label {
    display: block;
    font-weight: 600;
    color: #1f2937;
    font-size: 15px;
    margin-bottom: 4px;
}
.sec-card__help {
    margin: 0;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    max-width: 460px;
}
.sec-card__note {
    margin: 6px 0 0;
    color: #4b5563;
    font-size: 12px;
}
.sec-card__error {
    margin: 12px 0 0;
    color: #b91c1c;
    font-size: 13px;
}
.sec-field {
    margin-top: 12px;
}
.sec-field__input {
    width: 320px;
    max-width: 100%;
    padding: 6px 8px;
    border: 1px solid #cbd2dc;
    border-radius: 4px;
    font-size: 14px;
}
.sec-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}
.sec-button {
    padding: 6px 14px;
    border: 1px solid #cbd2dc;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
}
.sec-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}
</style>
